import { Stack } from '@mui/material';

import { COLORS } from '@/styles/tokens/colors';
import { MUI_INPUT_HEIGHT_PX } from '@/styles/tokens/inputHeights';

import { Button } from '../Button/Button';
import { ButtonVariant } from '../Button/types';
import { FormControl } from '../FormControl';
import {
  BaseButtonGroupInputProps,
  ButtonGroupVariant,
  FormControlButtonGroupInputProps,
  HelpTextVariant,
} from '../inputTypes';

export interface ButtonGroupProps<V = unknown>
  extends FormControlButtonGroupInputProps<V> {
  label: string;
  hideLabel?: boolean;
  id?: string;
  contextualHelp?: JSX.Element;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
}

function getBackgroundColorForVariant(variant: ButtonGroupVariant) {
  switch (variant) {
    case 'dark':
      return COLORS.GRAY[300];
    case 'light':
    default:
      return COLORS.GRAY[100];
  }
}

function BaseButtonGroupInput({
  options,
  variant = 'light',
  value: selectedValue,
  error,
  isInput,
  ...inputProps
}: BaseButtonGroupInputProps<unknown>) {
  const { disabled, sx, onChange, name, inputRef } = inputProps;

  let border = 'none';
  if (error) {
    border = `solid 1px ${COLORS.FUNCTIONAL.ERROR.DEFAULT}`;
  } else if (isInput) {
    border = `solid 1px ${COLORS.GRAY[300]}`;
  }

  const containerSx = Object.assign(
    {
      bgcolor: getBackgroundColorForVariant(variant),
      borderRadius: 1,
      border,
      overflow: 'hidden',
      listStyleType: 'none',
      height: isInput ? MUI_INPUT_HEIGHT_PX : undefined,
      p: isInput ? 0 : 0.5,
      m: 0,
    },
    sx
  );

  return (
    <Stack
      component="ul"
      role="group"
      direction="row"
      sx={containerSx}
      tabIndex={-1}
      ref={inputRef}
      id={inputProps.id}
      aria-labelledby={inputProps['aria-labelledby']}
    >
      {options.map(({ value, display, buttonProps = {} }, i) => {
        const isSelected = value === selectedValue;
        let buttonVariant: ButtonVariant = 'inactive-group-element';
        if (isSelected) {
          if (isInput) {
            buttonVariant = 'primary';
          } else {
            buttonVariant = 'active-group-element';
          }
        } else if (variant === 'dark') {
          buttonVariant = 'inactive-group-element-dark';
        }
        const buttonStyles = {
          sx: {
            whiteSpace: 'nowrap',
            px: 1.5,
            height: isInput ? '100%' : undefined,
          },
        };

        const buttonPropsWithStyles = {
          ...buttonStyles,
          ...buttonProps,
        };

        return (
          <li
            key={i}
            style={{
              flex: 1,
            }}
          >
            <Button
              {...buttonPropsWithStyles}
              variant={buttonVariant}
              value={value}
              disabled={disabled || buttonProps.disabled}
              aria-pressed={isSelected}
              size="_bg"
              fullWidth
              name={name}
              onClick={(event) => onChange(event, value)}
              data-testid={`button-group-${value ?? ''}`}
            >
              {display}
            </Button>
          </li>
        );
      })}
    </Stack>
  );
}

export function ButtonGroup<V>({
  id,
  label,
  hideLabel,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  ...inputProps
}: ButtonGroupProps<V>) {
  return (
    <FormControl<BaseButtonGroupInputProps<V>>
      id={id}
      required={inputProps.required}
      contextualHelp={contextualHelp}
      component={BaseButtonGroupInput}
      label={label}
      hideLabel={hideLabel}
      errorMessage={errorMessage}
      inputProps={inputProps}
      associateLabelWithAria={true}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
    />
  );
}
